import {useEffect,useRef,useState} from 'react';

/** A single automatically generated Secret Link. The token only unlocks an
 * offer lookup; the existing customer session, live source catalog, TEST
 * checkout gate, Stripe signed webhook and credit ledger remain authoritative.
 * Current payment slice supports only service/package credits. */
type SecretOffer={
  linkId:string;productId:string;locationCode:string;category:string;
  title:string;priceCents:number|null;sourceTag:string;publiclyListed:boolean;
};
type Readiness={testCheckoutApiEnabled:boolean};
const hex=/^[0-9a-f]{64}$/;
const uuid=/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
const price=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD',
}).format(cents/100);

export function SecretLinkWorkspace({token,isGuestAccount,onLogin,onLocationResolved}:{
  token:string;isGuestAccount:boolean;onLogin:()=>void;
  onLocationResolved:(code:string)=>void;
}){
  const [offer,setOffer]=useState<SecretOffer|null>(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [readiness,setReadiness]=useState<Readiness|null>(null);
  const [checkoutBusy,setCheckoutBusy]=useState(false);
  const [checkoutError,setCheckoutError]=useState('');
  const idempotencyKey=useRef(crypto.randomUUID());

  useEffect(()=>{
    const controller=new AbortController();
    setLoading(true);setError('');setOffer(null);setReadiness(null);
    if(!hex.test(token)){
      setError('This Secret Link is unavailable.');setLoading(false);
      return ()=>controller.abort();
    }
    void fetch('/api/v1/shop/secret-links/'+token,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('SECRET_LINK_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:SecretOffer};
      const item=body.data;
      if(!body.ok||!item||!uuid.test(item.productId)||
        !['2026-10-29','OWNER_CREATED'].includes(item.sourceTag)||
        !/^[A-Z0-9_-]{2,64}$/.test(item.locationCode)||
        typeof item.title!=='string'||!item.title.trim()||
        typeof item.category!=='string')throw Error('INVALID_SECRET_LINK');
      if(controller.signal.aborted)return;
      setOffer(item);
      onLocationResolved(item.locationCode);
    }).catch(()=>{
      if(!controller.signal.aborted)setError('This Secret Link is unavailable.');
    }).finally(()=>{
      if(!controller.signal.aborted)setLoading(false);
    });
    return ()=>controller.abort();
  },[token]);

  useEffect(()=>{
    if(!offer)return;
    const controller=new AbortController();
    void fetch('/api/v1/shop/stripe/readiness',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('NOT_AVAILABLE');
      const body=await response.json() as {ok:boolean;data?:Readiness};
      if(!controller.signal.aborted&&body.ok&&
        typeof body.data?.testCheckoutApiEnabled==='boolean')
        setReadiness(body.data);
    }).catch(()=>{
      if(!controller.signal.aborted)setReadiness(null);
    });
    return ()=>controller.abort();
  },[offer]);

  const supported=offer!==null&&
    (offer.category==='services'||offer.category==='packages')&&
    Number.isSafeInteger(offer.priceCents)&&Number(offer.priceCents)>0;
  const canCheckout=supported&&readiness?.testCheckoutApiEnabled===true;

  const startCheckout=async()=>{
    if(!offer||!canCheckout||!isGuestAccount||checkoutBusy)return;
    setCheckoutBusy(true);setCheckoutError('');
    try{
      const response=await fetch('/api/v1/shop/stripe/test-checkout',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          location:offer.locationCode,sourceTag:'2026-10-29',
          secretToken:token,idempotencyKey:idempotencyKey.current,
          lines:[{productId:offer.productId,quantity:1}],
        }),
      });
      if(!response.ok){
        if(response.status===409)throw Error(
          'This offer or its price has changed. Please review the Secret Link.');
        if(response.status===401)throw Error('Log in to complete checkout.');
        throw Error('Secure test checkout is temporarily unavailable.');
      }
      const body=await response.json() as {ok:boolean;data?:{
        orderId:string;checkoutUrl:string;paymentStatus:string}};
      if(!body.ok||body.data?.paymentStatus!=='UNPAID_TEST_SESSION'||
        !uuid.test(body.data.orderId)||!body.data.checkoutUrl)
        throw Error('Invalid checkout response.');
      const destination=new URL(body.data.checkoutUrl);
      if(destination.protocol!=='https:'||
        destination.hostname!=='checkout.stripe.com')
        throw Error('Invalid secure checkout destination.');
      // A Stripe test-session redirect never confirms payment. Signed webhook
      // confirmation and durable credit issuance are shown on /store only.
      window.location.assign(destination.href);
    }catch(cause){
      setCheckoutError(cause instanceof Error?cause.message:
        'Secure test checkout is temporarily unavailable.');
      setCheckoutBusy(false);
    }
  };

  return <section className="ecfs-public-content ecfs-public-commerce"
    aria-label="Secret Link offer">
    <h1>Secret Link</h1>
    {loading&&<p role="status">Loading your offer…</p>}
    {error&&<p role="alert">{error}</p>}
    {offer&&<article className="ecfs-public-product-card">
      <div className="ecfs-public-product-copy">
        <h2>{offer.title}</h2>
        <p>East Coast Float Spa {offer.locationCode==='PR'?'Princeton':
          offer.locationCode==='YD'?'Yardley':
          offer.locationCode==='WC'?'West Chester':offer.locationCode}</p>
      </div>
      <div className="ecfs-public-product-purchase">
        {Number.isSafeInteger(offer.priceCents)&&Number(offer.priceCents)>=0&&
          <strong>{price(Number(offer.priceCents))}</strong>}
      </div>
      {canCheckout
        ?isGuestAccount
          ?<button type="button" disabled={checkoutBusy}
            onClick={()=>void startCheckout()}>
            {checkoutBusy?'Opening Stripe Checkout…':'Continue to Stripe Test Checkout'}
          </button>
          :<button type="button" onClick={onLogin}>Log In to Checkout</button>
        :<p role="status">Online payment is not yet available for this offer.
          No purchase has been made.</p>}
      {checkoutError&&<p role="alert">{checkoutError}</p>}
    </article>}
  </section>;
}
